/**
 * The comments pane: every record's thread, always at hand in a collapsible pane docked to the
 * right of the content (UI-STANDARDS.md → Related panels). A record declares its thread with
 * `<RecordComments entityType entityId />`; the shell renders the pane. Collapsed, a slim rail keeps
 * the count visible; on narrow screens the pane becomes an overlay drawer. Whether it is open is a
 * per-viewer convenience kept in localStorage; nothing of the thread itself lives there.
 */
import { createContext, useContext, useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Icon, IconButton, useLocalStorage } from "@crc/ui";
import { api } from "../lib/api.ts";
import { CommentsPanel } from "../pages/shared/Panels.tsx";

interface Owner { entityType: string; entityId: string; title?: string }
interface PaneContext { owner: Owner | null; setOwner: (o: Owner | null) => void }

const Ctx = createContext<PaneContext>({ owner: null, setOwner: () => {} });

export function CommentsPaneProvider({ children }: { children: ReactNode }) {
  const [owner, setOwner] = useState<Owner | null>(null);
  const value = useMemo(() => ({ owner, setOwner }), [owner]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Placed anywhere in a record screen: registers the record's thread with the pane while mounted. */
export function RecordComments({ entityType, entityId, title }: Owner) {
  const { setOwner } = useContext(Ctx);
  useEffect(() => {
    setOwner({ entityType, entityId, title });
    return () => setOwner(null);
  }, [entityType, entityId, title, setOwner]);
  return null;
}

export function CommentsPane() {
  const { owner } = useContext(Ctx);
  const [collapsed, setCollapsed] = useLocalStorage<boolean>("comments-pane-collapsed", false);
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(max-width: 1100px)").matches : false));
  const panelId = useId();
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1100px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  // the same query the panel runs, so the rail shows the count while the panel is unmounted
  const { data } = useQuery({
    queryKey: ["comments", owner?.entityType ?? "", owner?.entityId ?? ""],
    queryFn: () => api.get<{ items: Array<{ deleted: boolean; resolvedAt: string | null; parentCommentId: string | null }> }>(`/comments?entityType=${owner!.entityType}&entityId=${owner!.entityId}`),
    enabled: !!owner,
  });
  const open = !!owner && !collapsed;
  useEffect(() => {
    if (!open || !narrow) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setCollapsed(true); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, narrow, setCollapsed]);
  if (!owner) return null;
  const live = (data?.items ?? []).filter((c) => !c.deleted);
  const openThreads = live.filter((c) => !c.parentCommentId && !c.resolvedAt).length;
  const count = live.length;
  const label = `Comments${count ? ` (${count})` : ""}`;

  if (collapsed) {
    return (
      <button type="button" className={`comments-rail${narrow ? " floating" : ""}`} aria-expanded={false} aria-controls={panelId} title="Show comments" onClick={() => setCollapsed(false)}>
        <Icon name="comment" size={18} />
        <span className="rail-label">{label}</span>
        {openThreads > 0 && <span className="rail-count" aria-label={`${openThreads} open`}>{openThreads}</span>}
      </button>
    );
  }
  return (
    <>
      {narrow && <div className="scrim show" onClick={() => setCollapsed(true)} />}
      <aside id={panelId} className={`comments-pane${narrow ? " overlay" : ""}`} aria-label="Comments">
        <div className="comments-pane-head">
          <Icon name="comment" size={16} />
          <b>{label}</b>
          {openThreads > 0 && <span className="rail-count">{openThreads} open</span>}
          <span style={{ flex: 1 }} />
          <IconButton icon="arrow" label="Collapse comments" aria-expanded={true} aria-controls={panelId} onClick={() => setCollapsed(true)} />
        </div>
        <div className="comments-pane-body">
          <CommentsPanel entityType={owner.entityType} entityId={owner.entityId} />
        </div>
      </aside>
    </>
  );
}
